import { Drawer, Button, message, Form, Select, Input } from "antd";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import WorkflowTarefaDrawer from "../../../components/tarefas-menu/_WorkflowTarefa";
import { createWorkflowTarefa } from "../../../store/workflow";
import unwrapAxiosPromise from "../../../util/unwrapAxiosPromise";
import useDispatchWithResult from "../../../hooks/useDispatchWithResult";
import ModuloTarefaModal from "../../../components/tarefa-modal/_WorkflowTarefaModal";
import { fetchVerificarLimite } from "../../../store/empresa";

export default function TarefasDrawer({ empresaId, unidade, modulo, origem, visible, onClose, onAfterClose }) {
  const { t } = useTranslation();
  const [criandoTarefa, setCriandoTarefa] = useState(false);
  const [tarefas, setTarefas] = useState([]);
  const [tiposTarefas, setTiposTarefas] = useState([]);
  const [tarefaSelecionada, setTarefaSelecionada] = useState(null);

  const dispatch = useDispatchWithResult();

  const fetchTarefas = async () => {
    try {
      const response = await axios.get(`/workflows/none/tarefas`, {
        params: {
          origem,
        },
      });
      setTarefas(response.data);
    } catch (e) {
      setTarefas([]);
    }
  };

  const handleUpdateTarefasESelecionada = async () => {
    try {
      const { data: novasTarefas } = await axios.get(`/workflows/none/tarefas`, {
        params: { origem },
      });

      setTarefas(novasTarefas);

      if (!tarefaSelecionada) return;

      const tarefaAtualizada = novasTarefas.find((t) => t.id === tarefaSelecionada.id);

      if (!tarefaAtualizada) return;

      try {
        const { data: logs } = await axios.get(`/workflows/${tarefaAtualizada.workflowId}/tarefas/${tarefaAtualizada.id}/logs`);

        setTarefaSelecionada({
          ...tarefaAtualizada,
          logs,
        });
      } catch (logError) {
        setTarefaSelecionada(tarefaAtualizada);
      }
    } catch (error) {
      console.error("Erro ao atualizar tarefas:", error);
      setTarefas([]);
    }
  };

  const fetchTiposTarefas = async () => {
    try {
      const response = await axios.get(`/workflows/none/tipos-tarefas?emUso=true`);
      setTiposTarefas(response.data);
    } catch (e) {
      setTiposTarefas([]);
    }
  };

  const handleCreateTarefas = () => {
    unwrapAxiosPromise(dispatch(fetchVerificarLimite())).then(() => {
      setCriandoTarefa(true);
    });
  };

  useEffect(() => {
    fetchTiposTarefas();
  }, [tarefas]);

  useEffect(() => {
    if (visible) {
      fetchTarefas();
    }
  }, [visible]);

  const handleCriarTarefa = async (Tarefa) => {
    try {
      await axios.post(`/workflows/none/tarefas`, {
        nome: Tarefa.nome,
        tipoTarefaId: Tarefa.tipoTarefaId,
        origem,
        modulo,
        empresa: parseInt(empresaId),
        unidade: unidade ? unidade.id : null,
      });

      await fetchTarefas();
    } catch (e) {
      console.error("Erro ao criar tarefa:", e);
    } finally {
      setCriandoTarefa(false);
    }

    setCriandoTarefa(false);
  };

  const handleRefreshTarefas = () => {
    fetchTarefas();
  };

  const fetchLogsDaTarefa = async (tarefaId, workflowId) => {
    try {
      const { data: logs } = await axios.get(`/workflows/${workflowId}/tarefas/${tarefaId}/logs`);
      setTarefaSelecionada((prev) => {
        if (!prev || prev.id !== tarefaId) return prev;
        return { ...prev, logs };
      });
    } catch (error) {
      message.error("Erro ao carregar logs.");
    }
  };

  return (
    <>
      <Drawer
        title={t("Tarefas vinculadas")}
        placement="right"
        width={400}
        bodyStyle={{ backgroundColor: "#eeeeee", paddingTop: "10px" }}
        headerStyle={{ backgroundColor: "#eeeeee" }}
        footerStyle={{ backgroundColor: "#eeeeee" }}
        onClose={onClose}
        afterOpenChange={(open) => {
          if (!open && typeof onAfterClose === "function") {
            onAfterClose();
          }
        }}
        open={visible}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTarefas}>
            {t("Criar Tarefa")}
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {tarefas.length === 0 ? (
            <div>{t("Nenhuma tarefa encontrada")}</div>
          ) : (
            tarefas.map((item) => (
              <WorkflowTarefaDrawer
                key={item.id}
                id={item.id}
                itens={item.itens}
                nome={item.nome}
                descricao={item.descricao}
                workflowId={item.workflowId}
                dataPrazo={item.dataPrazo || null}
                membros={item.membros || []}
                anexos={item.anexos || []}
                dataEntrega={item.dataEntrega || null}
                dataArquivamento={item.dataArquivamento || null}
                tipoTarefa={item.tipoTarefa || null}
                onRefresh={handleRefreshTarefas}
                onClick={async () => {
                  setTarefaSelecionada(item);
                }}
              />
            ))
          )}
        </div>
      </Drawer>

      <CriarTarefaDrawer visible={criandoTarefa} onClose={() => setCriandoTarefa(false)} onCreate={handleCriarTarefa} empresaId={empresaId} tiposTarefas={tiposTarefas} />
      {tarefaSelecionada && (
        <ModuloTarefaModal
          tarefa={{
            id: tarefaSelecionada.id,
            nome: tarefaSelecionada.nome,
            descricao: tarefaSelecionada.descricao,
            dataPrazo: tarefaSelecionada.dataPrazo,
            dataEntrega: tarefaSelecionada.dataEntrega,
            dataArquivamento: tarefaSelecionada.dataArquivamento,
            modulo: tarefaSelecionada.modulo,
            etapa: tarefaSelecionada.etapa,
            tipoTarefa: tarefaSelecionada.tipoTarefa,
            membros: tarefaSelecionada.membros || [],
            anexos: tarefaSelecionada.anexos || [],
            itens: tarefaSelecionada.itens || [],
            logs: tarefaSelecionada.logs || [],
            acesso: tarefaSelecionada.acesso,
            empresa: tarefaSelecionada.empresa,
            observacao: tarefaSelecionada.observacao,
            unidade: tarefaSelecionada.unidade,
          }}
          workflow={{
            id: tarefaSelecionada.workflowId,
            nome: "Workflow",
          }}
          usuarioId={99}
          isClienteOperador={true}
          isClienteGestor={true}
          onClose={() => setTarefaSelecionada(null)}
          onUpdate={handleUpdateTarefasESelecionada}
          onCancel={() => {
            setTarefaSelecionada(null);
          }}
          onFetchLogs={() => fetchLogsDaTarefa(tarefaSelecionada.id, tarefaSelecionada.workflowId)}
        />
      )}
    </>
  );
}

TarefasDrawer.propTypes = {
  empresaId: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onAfterClose: PropTypes.func,
};

function CriarTarefaDrawer({ visible, onClose, onCreate, tiposTarefas = [] }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const handleFinish = (values) => {
    if (!values.tipoTarefaId) {
      message.warning(t("Selecione o tipo da tarefa"));
      return;
    }

    onCreate({
      id: Date.now(),
      tipoTarefaId: values.tipoTarefaId,
      nome: values.nome,
    });

    form.resetFields();
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={t("Criar nova tarefa")}
      placement="right"
      width={400}
      onClose={handleClose}
      open={visible}
      footer={
        <>
          <Button onClick={handleClose}>{t("Cancelar")}</Button>
          <Button type="primary" onClick={() => form.submit()} style={{ marginLeft: 8 }}>
            {t("Salvar")}
          </Button>
        </>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="tipoTarefaId" label={t("Tipo de Tarefa")} rules={[{ required: true, message: t("Selecione o tipo da tarefa") }]}>
          <Select showSearch placeholder={t("Selecione o tipo")} optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
            {tiposTarefas.map((tipo) => (
              <Select.Option key={tipo.id} value={tipo.id}>
                {tipo.nome}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="nome" label={t("Nome")} rules={[{ required: true, message: t("O nome é obrigatório") }]}>
          <Input placeholder={t("Digite o nome da tarefa")} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

CriarTarefaDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  empresaId: PropTypes.number,
  tiposTarefas: PropTypes.array,
};
